/*
  MF Simulator: Carousel
*/

import React, { useLayoutEffect, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import Youtube from './Youtube';
import { trackClick } from '../utils/analytics';

const Carousel = ({ onClose, onVideoSelect, lan, hreflang, config, currentVideoIndex }) => {
  const { i18n } = useTranslation();
  const [youtubeVideo, setYoutubeVideo] = useState(null);

  // Pull locale-specific config safely
  const localeVideos = config?.videos?.[hreflang];
  const localeSimulator = config?.simulator?.[hreflang];
  const whatIsPresby = localeVideos?.what_is_presby;

  // Define video items in fixed order: Shopping -> Driving -> Dining
  const videoItems = [
    {
      header: <Trans i18nKey="carousel.titles.shopping" />,
      image: localeVideos?.main?.shopping?.image,
      youtube: false,
      link: false,
      target: localeVideos?.main?.shopping?.video
    },
    {
      header: <Trans i18nKey="carousel.titles.driving" />,
      image: localeVideos?.main?.driving?.image,
      youtube: false,
      link: false,
      target: localeVideos?.main?.driving?.video
    },
    {
      header: <Trans i18nKey="carousel.titles.dining" />,
      image: localeVideos?.main?.dining?.image,
      youtube: false,
      link: false,
      target: localeVideos?.main?.dining?.video
    }
  ].filter(item => item.image && item.target);

  const staticItems = [
    whatIsPresby?.target && (whatIsPresby?.type === 'youtube' || whatIsPresby?.type === 'link')
      ? {
          header: <Trans i18nKey="carousel.titles.what" />,
          image: "/img/carousel/what-presby.png",
          youtube: whatIsPresby.type === 'youtube',
          link: whatIsPresby.type === 'link',
          target: whatIsPresby.target
        }
      : null,
    localeSimulator?.why_choose_acuvue
      ? {
          header: <Trans i18nKey="carousel.titles.why" components={{ sup: <sup /> }} />,
          image: localeSimulator.image || "/img/carousel/why-acuvue.jpeg",
          youtube: false,
          link: true,
          target: localeSimulator.why_choose_acuvue
        }
      : null
  ].filter(Boolean);

  // Create reordered array starting with the current video
  const reorderedItems = () => {
    if (!videoItems.length) {
      return staticItems;
    }

    const safeIndex = currentVideoIndex % videoItems.length;
    const result = [];

    result.push(videoItems[safeIndex]);

    for (let i = 1; i < videoItems.length; i++) {
      const nextIndex = (safeIndex + i) % videoItems.length;
      result.push(videoItems[nextIndex]);
    }

    return [...result, ...staticItems];
  };

  const items = reorderedItems();

  useLayoutEffect(() => {
    i18n.changeLanguage(lan);
  }, [i18n, lan]);

  const handleItemClick = (item) => {
    const i18nKey = item.header?.props?.i18nKey;
    const itemText = i18nKey
      ? i18nKey.split('.').pop().charAt(0).toUpperCase() + i18nKey.split('.').pop().slice(1)
      : 'Unknown';

    trackClick({
      componentName: 'carousel overlay',
      type: item.link ? 'link_click' : 'button_click',
      linkText: itemText,
      linkUrl: item.target,
      outbound: item.link || item.youtube
    });

    if (item.link) {
      window.open(item.target, '_blank', 'noopener,noreferrer');
    } else if (item.youtube) {
      const separator = item.target.includes('?') ? '&' : '?';
      setYoutubeVideo(`${item.target}${separator}autoplay=1`);
    } else {
      onVideoSelect(item.target, true);
      onClose();
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target.classList.contains('carousel-overlay')) {
      onClose();
    }
  };

  const handleCloseYoutube = () => {
    setYoutubeVideo(null);
  };

  return (
    <div className="carousel-overlay" onClick={handleOverlayClick}>
      <button className="btn-close" onClick={onClose} aria-label="Close"></button>
      <div className="carousel-container">
        {items.map((item, index) => (
          <div key={index} className="card" onClick={() => handleItemClick(item)}>
            <div className="card-header">{item.header}</div>
            <div className="card-body">
              <img src={item.image} alt="" className="img-fluid" />
            </div>
          </div>
        ))}
      </div>
      {youtubeVideo && (
        <Youtube video={youtubeVideo} onClose={handleCloseYoutube} />
      )}
    </div>
  );
};

export default Carousel;